//-- Copyright (c) 2021 TMT Insights Group, LLC
import { MouseEventHandler, useContext } from 'react';
import styled from '@emotion/styled';
import { Chip, Typography } from '@mui/material';

import { ConfigContext } from '@/contextAPI/configContext';
import type { TitleEntity } from '@/redux/slices/slice_types';
import { generateHexFromString } from '@/_utils/colorUtils';
import PolarisIcon from '@/components/_common/PolarisIcon';
import focusConfig from '@/components/Focus/configs';

type VersionsChipSize = 'medium' | 'small';

type VersionsChipProps = {
  isSelected?: boolean;
  onClick?: (versionName: string, versions: TitleEntity[]) => void;
  size?: VersionsChipSize;
  versionName: string;
  versions?: TitleEntity[];
};

const VersionsChip = ({ isSelected = false, onClick, size = 'medium', versionName, versions = [] }: VersionsChipProps) => {
  const isFocus = window.location.pathname.includes('focus');
  const versionColors = useContext(ConfigContext)?.versionColors || [];
  const focusVersionColor =
    isFocus && focusConfig.versionColors && focusConfig.versionColors[versionName.toLowerCase()]
      ? focusConfig.versionColors[versionName.toLowerCase()]
      : undefined;

  const color = focusVersionColor || versionColors[versionName.toLowerCase()] || generateHexFromString(versionName) || '#000';
  const isClickable = Boolean(onClick);
  const iconHeight = size === 'small' ? 13 : 15;
  const fontSize = size === 'small' ? 12 : 14;

  const handleClick: MouseEventHandler<HTMLDivElement> = (event) => {
    if (!onClick) return;
    event.stopPropagation();
    onClick(versionName, versions);
  };

  return (
    <StyledChip
      bg={color}
      clickable={isClickable}
      color="primary"
      icon={isClickable ? <PolarisIcon icon={isSelected ? 'check' : 'add'} height={iconHeight} /> : undefined}
      isSelected={isSelected}
      label={
        <Typography noWrap sx={{ fontSize, lineHeight: 1 }}>
          {versionName.toUpperCase()}
        </Typography>
      }
      onClick={isClickable ? handleClick : undefined}
      size={size}
      sx={{ borderRadius: 5 }}
      variant="outlined"
    />
  );
};

export default VersionsChip;

const StyledChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'isSelected' && prop !== 'bg'
})<{ isSelected: boolean; bg: string; size?: VersionsChipSize }>`
  margin: ${({ size, theme }) => (size === 'small' ? theme.spacing(0.25, 0, 0.25, 0.75) : theme.spacing(0.25, 0, 0.25, 1))};
  border: 1px solid;
  color: ${({ isSelected, bg, theme }) => (isSelected ? theme.palette.background.default : bg)};
  border-color: ${({ bg }) => bg};

  & .MuiChip-label {
    padding-left: ${({ size, theme }) => (size === 'small' ? theme.spacing(0.75) : theme.spacing(1))};
    padding-right: ${({ size, theme }) => (size === 'small' ? theme.spacing(0.75) : theme.spacing(1))};
  }

  & .MuiChip-icon {
    margin-left: ${({ size, theme }) => (size === 'small' ? theme.spacing(0.5) : theme.spacing(0.75))};
    margin-right: ${({ size, theme }) => (size === 'small' ? theme.spacing(-0.25) : theme.spacing(0))};
  }

  & {
    ${({ isSelected, bg, theme, size }) =>
      theme.customOverrides.TitleVersionChip
        ? `
    background-color: ${size === 'small' ? `${bg}1A` : isSelected ? bg : theme.customOverrides.TitleVersionChip.backgroundColor};
    color: ${isSelected ? theme.customOverrides.TitleVersionChip.color : bg};
    `
        : `
    background-color: ${size === 'small' ? `${bg}1A` : isSelected ? bg : 'transparent'};
    `}
  }

  &.MuiChip-clickable:hover {
    ${({ isSelected, bg, theme }) =>
      theme.customOverrides.TitleVersionChip
        ? `
    background-color: ${!isSelected ? bg : theme.customOverrides.TitleVersionChip.backgroundColor};
    color: ${!isSelected ? theme.customOverrides.TitleVersionChip.color : bg};
    `
        : `
    background-color: ${isSelected ? bg : 'transparent'};
    `}
  }
`;
